import React, { useRef } from 'react';
import { Box, Typography } from '@mui/material';
import { gsap } from 'gsap';
import HeroScene from '../fx/HeroScene';
import { useGsap } from '../fx/motion';
import { orchard } from '../../theme';

/* Shared hero for the teacher, admin and parent dashboards: a night-orchard
   gradient with the three.js scene behind the content. Children (usually a
   StatTile strip) render under the title block; `avatar` (optional) sits
   left of the title. Elements marked `data-hero-item` rise in one after
   another on mount. */
export default function DashboardHero({
  eyebrow,
  title,
  subtitle,
  actions,
  avatar,
  children,
  sceneDensity = 1,
}) {
  const rootRef = useRef(null);

  useGsap(
    () => {
      gsap.from('[data-hero-item]', {
        autoAlpha: 0,
        y: 18,
        duration: 0.8,
        ease: 'power3.out',
        stagger: 0.08,
        delay: 0.05,
        clearProps: 'opacity,visibility,transform',
      });
    },
    [],
    rootRef
  );

  return (
    <Box
      ref={rootRef}
      component="section"
      sx={{
        position: 'relative',
        isolation: 'isolate',
        overflow: 'hidden',
        color: '#fff',
        borderRadius: { xs: '20px', md: '28px' },
        mt: { xs: 1, md: 2 },
        mb: { xs: 2.5, md: 3.5 },
        p: { xs: 2.25, sm: 3.5, md: 4.5 },
        boxShadow: '0 24px 48px -28px rgba(140,18,48,0.7)',
      }}
    >
      <HeroScene density={sceneDensity} />
      {/* Keeps the text legible over the brightest part of the scene */}
      <Box
        aria-hidden="true"
        sx={{
          position: 'absolute',
          inset: 0,
          pointerEvents: 'none',
          background: {
            xs: 'linear-gradient(180deg, rgba(22,16,32,0.55) 0%, rgba(22,16,32,0.2) 100%)',
            md: 'linear-gradient(90deg, rgba(22,16,32,0.7) 0%, rgba(22,16,32,0.2) 62%, transparent 100%)',
          },
        }}
      />
      <Box sx={{ position: 'relative', zIndex: 1 }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: { xs: 'stretch', md: 'flex-end' },
            justifyContent: 'space-between',
            flexDirection: { xs: 'column', md: 'row' },
            gap: { xs: 2, md: 3 },
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: { xs: 1.5, md: 2.25 },
              minWidth: 0,
            }}
          >
            {avatar && (
              <Box data-hero-item sx={{ flexShrink: 0, display: 'flex' }}>
                {avatar}
              </Box>
            )}
            <Box sx={{ minWidth: 0 }}>
              {eyebrow && (
                <Typography
                  data-hero-item
                  variant="overline"
                  sx={{
                    display: 'block',
                    color: orchard.honey,
                    lineHeight: 1.6,
                    fontSize: { xs: '0.68rem', sm: '0.75rem' },
                  }}
                >
                  {eyebrow}
                </Typography>
              )}
              <Typography
                data-hero-item
                component="h1"
                sx={{
                  fontFamily: orchard.fontDisplay,
                  fontWeight: 700,
                  letterSpacing: '-0.02em',
                  lineHeight: 1.12,
                  color: '#fff',
                  fontSize: { xs: '1.4rem', sm: '1.9rem', md: '2.4rem' },
                  overflowWrap: 'anywhere',
                }}
              >
                {title}
              </Typography>
              {subtitle && (
                <Typography
                  data-hero-item
                  sx={{
                    mt: 0.75,
                    color: 'rgba(255,255,255,0.78)',
                    fontSize: { xs: '0.9rem', sm: '1rem' },
                    maxWidth: 560,
                  }}
                >
                  {subtitle}
                </Typography>
              )}
            </Box>
          </Box>
          {actions && (
            <Box
              data-hero-item
              sx={{
                display: 'flex',
                gap: 1,
                flexWrap: 'wrap',
                flexShrink: 0,
              }}
            >
              {actions}
            </Box>
          )}
        </Box>
        {children && <Box sx={{ mt: { xs: 2, md: 3 } }}>{children}</Box>}
      </Box>
    </Box>
  );
}
